import React, { useEffect, useState } from "react";
import { Form, Input, Button, message } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { useAuth } from "context/AuthContextProvider";
import { useResetPassword } from "hooks/mutations/auth";
import { translate } from "shared/languages";
import { AuthCard, AuthHeading, FieldLabel } from "./AuthUi";
import { textPx } from "shared/constants/zoom";

/** New-password step — centered card; validation + mutation unchanged. */
const ResetPassword: React.FC = () => {
  const phone_number = sessionStorage.getItem("reset_number");
  const navigate = useNavigate();
  const { getPhoneProof } = useAuth();
  const { mutate: resetPassword, isPending } = useResetPassword();
  const [fetchingProof, setFetchingProof] = useState(false);
  const submitting = fetchingProof || isPending;

  // Extract the phone number from session storage
  useEffect(() => {
    if (!phone_number) {
      message.error(translate("messages.error.general"));
      navigate(PATHS.LOGIN);
    }
    // eslint-disable-next-line
  }, [phone_number]);

  const handleSubmit = async (values: { password: string; confirmPassword: string }) => {
    if (!phone_number || submitting) {
      if (!phone_number) message.error(translate("messages.error.general"));
      return;
    }
    setFetchingProof(true);
    const firebase_id_token = await getPhoneProof().finally(() => setFetchingProof(false));
    if (!firebase_id_token) {
      message.error(translate("messages.error.general"));
      sessionStorage.removeItem("reset_number");
      navigate(PATHS.FORGET_PASSWORD);
      return;
    }
    resetPassword({ password: values.password, phone_number, firebase_id_token });
  };

  return (
    <AuthCard onBack={() => navigate(PATHS.LOGIN)}>
      <AuthHeading>{translate("title.reset_password")}</AuthHeading>
      <div style={{ height: 26 }} />

      <Form onFinish={handleSubmit} layout="vertical" requiredMark={false} initialValues={{ password: "", confirmPassword: "" }}>
        <Form.Item
          name="password"
          label={<FieldLabel>{translate("label.password")}</FieldLabel>}
          rules={[
            {
              required: true,
              message: `${translate("form.error.input")} ${translate("label.password")} ${translate("form.error.input_end")}`,
            },
            { min: 6, message: translate("form.error.password") },
          ]}
          hasFeedback
        >
          <Input.Password placeholder="••••••••" />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label={<FieldLabel>{translate("label.confirm_password")}</FieldLabel>}
          dependencies={["password"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: `${translate("form.error.input")} ${translate("label.confirm_password")} ${translate(
                "form.error.input_end"
              )}`,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(translate("form.error.two_password")));
              },
            }),
          ]}
        >
          <Input.Password placeholder="••••••••" />
        </Form.Item>

        <Form.Item style={{ marginTop: 20, marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={submitting} style={{ width: "100%" }}>
            {translate("button.confirm")} <ArrowRightOutlined style={{ fontSize: textPx(14) }} />
          </Button>
        </Form.Item>
      </Form>
    </AuthCard>
  );
};

export default ResetPassword;
